import type { AIPresenceState } from '@/common/components/presence/types';

/** Halo extends ~30% beyond the orb edge (locked plan budget). */
export const GLOW_HALO_INSET_RATIO = -0.3;

/** Blur radius scales with orb size. */
export const GLOW_BLUR_FACTOR = 0.22;

export const SPEAKING_GLOW_ALPHA_BASE = 0.18;
export const SPEAKING_GLOW_ALPHA_PUNCH_FACTOR = 0.14;
export const SPEAKING_GLOW_ALPHA_MAX = 0.32;

export const NON_SPEAKING_GLOW_ALPHA_MAX = 0.02;

/** Diameter pulse while speaking (punch=1): +24%. */
export const SPEAKING_GLOW_SCALE_PUNCH_FACTOR = 0.24;

/** Peak layout scale; canvas backing store is allocated at this size so punch never upscales a bitmap. */
export const SPEAKING_GLOW_SCALE_MAX = 1 + SPEAKING_GLOW_SCALE_PUNCH_FACTOR;

export function computeGlowBlur(size: number): number {
  return GLOW_BLUR_FACTOR * size;
}

/**
 * Halo box inset as a percent of the layout shell.
 * Rest is −30%. Grows with visual scale so the glow tracks a canvas-drawn pulse
 * (CSS-scaling the halo+filter would re-rasterize and flicker).
 */
export function computeGlowHaloInsetPercent(scale = 1): number {
  const restDiameterFactor = 1 - 2 * GLOW_HALO_INSET_RATIO;
  return -((restDiameterFactor * Math.max(scale, 0) - 1) / 2) * 100;
}

export function computePulseCanvasCssSize(layoutSize: number): number {
  return layoutSize * SPEAKING_GLOW_SCALE_MAX;
}

/** How far the peak pulse reaches past the resting edge; reserve it before adjacent text. */
export function computeSpeakingPulseOverhangPx(layoutSize: number): number {
  return Math.ceil((layoutSize * (SPEAKING_GLOW_SCALE_MAX - 1)) / 2);
}

export function computeSpeakingGlowAlpha(punch: number): number {
  const clampedPunch = Math.max(0, punch);
  return Math.min(
    SPEAKING_GLOW_ALPHA_MAX,
    SPEAKING_GLOW_ALPHA_BASE + SPEAKING_GLOW_ALPHA_PUNCH_FACTOR * clampedPunch,
  );
}

export function computeNonSpeakingGlowAlpha(): number {
  return NON_SPEAKING_GLOW_ALPHA_MAX;
}

export function computeSpeakingGlowScale(punch: number): number {
  const clampedPunch = Math.min(1, Math.max(0, punch));
  return 1 + clampedPunch * SPEAKING_GLOW_SCALE_PUNCH_FACTOR;
}

/** Punch scale applied in the canvas CTM — not as a CSS transform on the bitmap. */
export function computeSpeakingDrawScale(state: AIPresenceState, punch: number): number {
  if (state !== 'speaking' || punch <= 0) return 1;
  return computeSpeakingGlowScale(punch);
}

export type PresenceGlowVisuals = {
  alpha: number;
  scale: number;
};

export function computePresenceGlowVisuals(params: {
  state: AIPresenceState;
  punch: number;
  reducedMotion: boolean;
}): PresenceGlowVisuals {
  const {
    state, punch, reducedMotion,
  } = params;
  const isSpeaking = state === 'speaking';

  if (reducedMotion) {
    return {
      alpha: isSpeaking ? computeSpeakingGlowAlpha(0) : computeNonSpeakingGlowAlpha(),
      scale: 1,
    };
  }

  if (isSpeaking && punch > 0) {
    return {
      alpha: computeSpeakingGlowAlpha(punch),
      scale: computeSpeakingGlowScale(punch),
    };
  }

  return {
    alpha: computeNonSpeakingGlowAlpha(),
    scale: 1,
  };
}
